import { take, call, put, select } from 'redux-saga/effects'
import * as types from '../../../../lib/actionTypes.es6'
import * as actions from './actions'
import {
  getGroupPayments,
  getGroupPayment,
  updateGroupPaymentStatus,
  getOmkGroup,
  getUserById
} from '../../../../lib/requests.es6'
import { GAloggerException } from '../../../../lib/monitoring.es6'
import { unAuthorizedNotification } from '../../../../lib/access.es6'

export function* getGroupPaymentsRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_GROUP_PAYMENTS_REQUEST)
    try {
      const response = yield call(getGroupPayments, request.payload)
      yield put(actions.getGroupPaymentsSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response && e.response.data) {
        errorMessage = e.response.data.message || e.response.data
      }
      yield put(actions.getGroupPaymentsFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* getGroupPaymentRequestWatcher() {
  for (;;) {
    const request = yield take(types.GET_GROUP_PAYMENT_REQUEST)
    try {
      const response = yield call(getGroupPayment, request.payload)
      yield put(actions.getGroupPaymentSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response && e.response.data) {
        errorMessage = e.response.data.message || e.response.data
      }
      yield put(actions.getGroupPaymentFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

export function* updateGroupPaymentStatusRequestWatcher() {
  for (;;) {
    const request = yield take(types.UPDATE_GROUP_PAYMENT_STATUS_REQUEST)
    try {
      const response = yield call(updateGroupPaymentStatus, request.payload)
      yield put(actions.updateGroupPaymentStatusSuccess(response.data))
    } catch (e) {
      let errorMessage = 'An unexpected error occurred'
      if (e.response && e.response.data) {
        errorMessage = e.response.data.message || e.response.data
      }
      yield put(actions.updateGroupPaymentStatusFailure(errorMessage))
      GAloggerException(e)
      unAuthorizedNotification(e)
    }
  }
}

// A group payment only carries a group guid and Keycloak user ids. These two
// lookups turn those into the names the dashboard shows. Both swallow their
// errors: a label that cannot be resolved just stays a guid, it is never worth
// failing the screen over (and must never throw, or the watcher dies).
function* lookupGroupLabel(guid) {
  try {
    const response = yield call(getOmkGroup, guid)
    const group = response.data || {}
    return {
      key: guid,
      value: { group_name: group.group_name, group_number: group.group_number }
    }
  } catch (e) {
    return null
  }
}

function* lookupUserLabel(userId) {
  try {
    const response = yield call(getUserById, userId)
    const user = response.data || {}
    const fullName = [user.firstName, user.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    const value = fullName || user.username || user.email
    return value ? { key: userId, value } : null
  } catch (e) {
    return null
  }
}

function collect(results) {
  return results.filter(Boolean).reduce(function(acc, entry) {
    acc[entry.key] = entry.value
    return acc
  }, {})
}

// Only look up what we do not already hold — the list re-dispatches on every
// page change and the same groups/makers recur across pages.
function unresolved(ids, cache) {
  const seen = {}
  return (ids || []).filter(function(id) {
    if (!id || seen[id] || cache[id]) return false
    seen[id] = true
    return true
  })
}

export function* resolveOmkPaymentLabelsRequestWatcher() {
  for (;;) {
    const request = yield take(types.RESOLVE_OMK_PAYMENT_LABELS_REQUEST)
    try {
      const payload = request.payload || {}
      const state = yield select(function(s) {
        return s.omkGroupPaymentData
      })
      const groupGuids = unresolved(payload.groupGuids, state.group_labels)
      const userIds = unresolved(payload.userIds, state.user_labels)
      if (groupGuids.length > 0 || userIds.length > 0) {
        const results = yield groupGuids
          .map(function(guid) {
            return call(lookupGroupLabel, guid)
          })
          .concat(
            userIds.map(function(userId) {
              return call(lookupUserLabel, userId)
            })
          )
        yield put(
          actions.resolveOmkPaymentLabelsSuccess({
            groups: collect(results.slice(0, groupGuids.length)),
            users: collect(results.slice(groupGuids.length))
          })
        )
      }
    } catch (e) {
      // Labels are cosmetic — log and leave the guids on screen.
      GAloggerException(e)
    }
  }
}
